import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { NgxGroupService } from '@go-modules/services/group/group.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxSessionService, SharePublicResponse } from '@go-modules/services/session/session.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { VIDEO_SHARE_SETTINGS } from 'go-modules/org-settings/org-settings.video-share.enum';
import { UserRole } from '@go-modules/interfaces';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';
import { ReviewerTourService } from '@go-modules/tours/reviewer-tour/reviewer-tour.service';
import { UserFormFieldComponent } from '../../user-form-field/user-form-field.component';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { noop } from 'angular';
import type { GuestReviewer } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { Group as GroupModel } from 'go-modules/models/group-dep/group.factory';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';

export interface VideoShareDialogData {
	orgSettings: OrgSettings;
	sessionId: number;
}

@Component({
	selector: 'video-share-dialog',
	templateUrl: './video-share-dialog.component.html',
	styleUrls: ['./video-share-dialog.component.scss'],
	standalone: false
})
export class VideoShareDialogComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(UserFormFieldComponent) public userFormFieldComponent: UserFormFieldComponent;
	public dialogTitleId = `video-share-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	public shareLink: string;
	public guestReviewersCount: number;
	public creatingLink$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public users: UserRole[] = [];
	public session: ReturnType<typeof SessionModel>;
	public group: ReturnType<typeof GroupModel>;
	public form = new UntypedFormGroup({
		childForm: new UntypedFormControl({})
	});
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
	public isSaving$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private destroy$ = new Subject<void>();
	public shouldShowGuestReviewers$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private shouldHighlightShareLink = false;
	private shareLinkInput?: ElementRef<HTMLInputElement>;

	@ViewChild('shareLinkInput')
	public set shareLinkInputSetter (input: ElementRef<HTMLInputElement> | undefined) {
		this.shareLinkInput = input;
		if (input && this.shouldHighlightShareLink) {
			setTimeout(() => this.highlightShareLink());
		}
	}

	constructor (
		@Inject(GO_MODAL_DATA) public data: VideoShareDialogData,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		private dialogRef: GoDialogRef,
		private dialog: MatDialog,
		private groupService: NgxGroupService,
		private sessionService: NgxSessionService,
		private reviewerTour: ReviewerTourService,
		private translate: TranslateService
	) {}

	public ngOnInit (): void {
		if (this.allowsInternalShare()) {
			this.groupService.getUsers(this.selectedService.getGroup().group_id).subscribe((res) => {
				this.users = res;
			});
		}
		this.group = this.selectedService.getGroup();
		this.sessionService.getSession(
			this.data.sessionId,
			['externalSessionUsers', 'externalSessionUsers.user', 'internalReviewers']
		).subscribe((session) => {
			this.session = session;
			this.shouldShowGuestReviewers$.next(
				this.sessionService.hasVideoSharePermission(this.Session.model(this.session))
			);

			this.guestReviewersCount = session.guest_reviewers.length;
			if (session.external_session) {
				this.shareLink = `${clientSettings.VideoShareUrl}/sessions/${session.external_session}`;
				this.triggerShareLinkHighlight();
			}
			this.loading$.next(false);
		});
	}

	public ngAfterViewInit (): void {
		if (this.showReviewersField()) {
			this.reviewerTour.getTour().start();
		}
		if (this.shouldHighlightShareLink) {
			this.highlightShareLink();
		}
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();

		this.isSaving$.complete();
	}

	public showReviewersField (): boolean {
		return this.isPrivateShare() ||
			this.isPublicAndPrivateShare() ||
			this.allowsInternalShare();
	}

	public showSeparator (): boolean {
		return (this.selectedService.getActivity().is_video_share_enabled ||
			this.selectedService.getGroup().hasInstructorRole(true)) &&
			(this.isPublicAndPrivateShare() ||
			(this.isPublicShare() && this.allowsInternalShare()));
	}

	public showInviteButtonAbove (): boolean {
		return this.showSeparator() && Object.keys(this.form.value.childForm).length > 0;
	}

	public isPrivateShare (): boolean {
		return this.data.orgSettings.allow_video_share === VIDEO_SHARE_SETTINGS.PRIVATE;
	}

	public isPublicShare (): boolean {
		return this.data.orgSettings.allow_video_share === VIDEO_SHARE_SETTINGS.PUBLIC;
	}

	public isPublicAndPrivateShare (): boolean {
		return this.data.orgSettings.allow_video_share === VIDEO_SHARE_SETTINGS.BOTH;
	}

	public isExternalVideoShareDisabled (): boolean {
		return this.data.orgSettings.allow_video_share === null ||
			(!this.selectedService.getActivity().is_video_share_enabled &&
			!this.selectedService.getGroup().hasInstructorRole(true));
	}

	public allowsInternalShare (): boolean {
		return this.data.orgSettings.internal_reviewers_enabled;
	}

	public allowsExternalShare (): boolean {
		return (this.isPublicAndPrivateShare() || this.isPublicShare()) &&
			(this.selectedService.getActivity().is_video_share_enabled ||
			this.selectedService.getGroup().hasInstructorRole(true));
	}

	public createPublicShareLink (): void  {
		this.creatingLink$.next(true);
		this.sessionService.sharePublic(this.data.sessionId).subscribe((res: SharePublicResponse) => {
			this.shareLink = `${clientSettings.VideoShareUrl}/sessions/${res.uuid}`;
			this.creatingLink$.next(false);
			this.triggerShareLinkHighlight();
		});
	}

	public expireAccess () {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = ConfirmDialogComponent.open(this.dialog, {
			data: {
				title: this.translate.instant('modal-expire-public-link-access_title'),
				message: this.translate.instant('modal-expire-public-link-access_text'),
				confirmText: this.translate.instant('session-editor-panel_expire-link'),
				cancelText: this.translate.instant('common_cancel')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				return this.Session.expirePublicLinkAccess({ session_id: this.session.session_id }).$promise
					.then(() => {
						// Guests with a null uuid have access from the public link and should be removed from the list
						this.session.guest_reviewers = this.session.guest_reviewers
							.filter((guest) => guest.uuid !== null);
						this.shareLink = '';
					}).catch(noop);
			}
		});
	}

	public sendInvite (): void  {
		const unsavedValue = this.userFormFieldComponent.userInputCtrl.value;
		if (unsavedValue) {
			const isValidEmail = new UntypedFormControl(unsavedValue, Validators.email).valid;
			if (isValidEmail) {
				this.userFormFieldComponent.add(this.userFormFieldComponent.userInput);
			}
		}
		if (!this.form.valid) {
			return;
		}
		if (this.form.pristine ||
			(!this.form.value.childForm.external_reviewer_emails.length &&
				!this.form.value.childForm.internal_reviewer_user_ids.length)) {
			return this.cancel();
		}

		this.isSaving$.next(true);
		this.sessionService.sharePrivate(
			this.data.sessionId,
			this.form.value.childForm
		).pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {
					const {invalidExternalUsers: invalidEmails, invalidInternalUsers: invalidUserIds } = res;
					const invalidUserNames = this.users
						.filter((user) => invalidUserIds.includes(user.user_id))
						.map((user) => `${user.first_name} ${user.last_name}`);
					this.dialogRef.close({ invalidEmails, invalidUserNames });
				},
				error: (err) => {
					this.dialogRef.close(err);
				}
			});
	}

	public onDeleteReviewer (guest: GuestReviewer) {
		guest.deleted_at = new Date();
	}

	public manageVideoSharing (): void  {
		this.dialogRef.close({ dismissed: true, openEditUserPanel: true });
	}

	public cancel (): void  {
		this.dialogRef.close({ dismissed: true, session: this.session });
	}

	public onShareLinkFocus (): void {
		this.highlightShareLink();
	}

	public onShareLinkMouseUp (event: MouseEvent): void {
		event.preventDefault();
		this.highlightShareLink();
	}

	public onShareLinkCopy (event: ClipboardEvent): void {
		if (!this.shareLink) {
			return;
		}

		this.highlightShareLink();

		if (event.clipboardData) {
			event.clipboardData.setData('text/plain', this.shareLink);
			event.preventDefault();
		}
	}

	private triggerShareLinkHighlight (): void {
		if (!this.shareLink) {
			return;
		}

		this.shouldHighlightShareLink = true;
		setTimeout(() => this.highlightShareLink());
	}

	private highlightShareLink (): void {
		if (!this.shareLink) {
			this.shouldHighlightShareLink = false;
			return;
		}

		const input = this.shareLinkInput?.nativeElement;

		if (!input) {
			return;
		}

		this.shouldHighlightShareLink = false;

		input.focus();
		input.select();

		this.selectAllText(input);
	}

	private selectAllText (input: HTMLInputElement): void {
		if (typeof input.setSelectionRange !== 'function') {
			return;
		}

		try {
			input.setSelectionRange(0, input.value.length);
		} catch {
			// ignore selection range errors
		}
	}
}
